<div class="card">
  <!--
    --fixed-header keeps the banner, stats cards and filter bar in place while only the table
    body scrolls. The tab chain below rebuilds the flex context that `scrollHeight="flex"`
    needs: the global `.card-content > p-table` rule is a direct-child selector, and these
    tables sit three levels deeper inside p-tabs.
  -->
  <div class="card-content card-content--fixed-header">

    <!--
      Health banner. Amber for "Pending" (no run history yet — a fresh deploy, which clears
      itself) and for "Stalled"; only the wording escalates. A red alarm on a state that
      resolves on its own would train people to ignore the banner.
    -->
    <div class="health-banner"
         *ngIf="!summaryLoading && summary"
         [class.health-banner--ok]="summary.severity === 'Ok'"
         [class.health-banner--warn]="summary.severity !== 'Ok'">
      <div class="health-banner-icon"><i class="pi" [ngClass]="bannerIcon"></i></div>
      <div class="health-banner-main">
        <span class="health-banner-title">{{ bannerTitle }}</span>
        <span class="health-banner-message">{{ summary.statusMessage }}</span>
      </div>
    </div>

    <div class="health-banner health-banner--warn" *ngIf="!summaryLoading && !summary">
      <div class="health-banner-icon"><i class="pi pi-exclamation-triangle"></i></div>
      <div class="health-banner-main">
        <span class="health-banner-title">Health status unavailable</span>
        <span class="health-banner-message">
          The health summary could not be loaded. The backend may be unreachable.
        </span>
      </div>
    </div>

    <!-- Summary cards -->
    <div class="stats-cards-row" *ngIf="summary">
      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">
            {{ summary.hoursSinceLastSuccess === null
                ? '—'
                : (summary.hoursSinceLastSuccess | number: '1.0-0') + 'h' }}
          </span>
          <span class="stats-card-label">Since Last Success</span>
          <span class="stats-card-sublabel">Contract reminder job</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-clock"></i></div>
      </div>
      <div class="stats-card stats-card--success">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ summary.emailsSentLast7Days }}</span>
          <span class="stats-card-label">Emails Sent</span>
          <span class="stats-card-sublabel">Last 7 days</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-send"></i></div>
      </div>
      <div class="stats-card stats-card--danger">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ summary.emailsFailedLast7Days }}</span>
          <span class="stats-card-label">Failed Sends</span>
          <span class="stats-card-sublabel">Last 7 days</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-times-circle"></i></div>
      </div>
      <div class="stats-card stats-card--warning">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ summary.upcomingRemindersCount }}</span>
          <span class="stats-card-label">Reminders Due</span>
          <span class="stats-card-sublabel">Next 30 days</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-calendar"></i></div>
      </div>
    </div>

    <!--
      Filters collapse behind the header's funnel icon, as on Audit Logs and the other list
      screens. One bar serves every tab; irrelevant fields hide with the active tab.
    -->
    <div class="filter-bar filter-bar-collapsible mb-4"
         [class.filter-bar-visible]="pageHeaderService.currentFiltersVisible">
      <div class="filter-bar-filters">
        <div class="filter-field" *ngIf="activeTab === '0'">
          <label class="filter-label">Status</label>
          <p-select [(ngModel)]="runStatus" [options]="statusOptions"
                    optionLabel="label" optionValue="value" [showClear]="true"
                    placeholder="All statuses" appendTo="body"
                    panelStyleClass="filter-dropdown-panel"
                    (onChange)="onRunsFilterChange()"></p-select>
        </div>

        <ng-container *ngIf="activeTab === '1' || activeTab === '3'">
          <div class="filter-field">
            <label class="filter-label">Type</label>
            <p-select [(ngModel)]="emailType" [options]="notificationTypes"
                      optionLabel="label" optionValue="value" [showClear]="true"
                      placeholder="All types" appendTo="body"
                      panelStyleClass="filter-dropdown-panel"
                      (onChange)="onEmailsFilterChange()"></p-select>
          </div>
          <div class="filter-field" *ngIf="activeTab === '1'">
            <label class="filter-label">Outcome</label>
            <p-select [(ngModel)]="emailOutcome" [options]="outcomeOptions"
                      optionLabel="label" optionValue="value" [showClear]="true"
                      placeholder="All outcomes" appendTo="body"
                      panelStyleClass="filter-dropdown-panel"
                      (onChange)="onEmailsFilterChange()"></p-select>
          </div>
          <div class="filter-field">
            <label class="filter-label">From</label>
            <p-datepicker [(ngModel)]="emailFrom" dateFormat="dd/mm/yy"
                          iconDisplay="input" [showIcon]="true" appendTo="body"
                          [maxDate]="emailTo || undefined"
                          (onSelect)="onEmailsFilterChange()"></p-datepicker>
          </div>
          <div class="filter-field">
            <label class="filter-label">To</label>
            <p-datepicker [(ngModel)]="emailTo" dateFormat="dd/mm/yy"
                          iconDisplay="input" [showIcon]="true" appendTo="body"
                          [minDate]="emailFrom || undefined"
                          (onSelect)="onEmailsFilterChange()"></p-datepicker>
          </div>
        </ng-container>

        <div class="filter-field" *ngIf="activeTab === '2'">
          <label class="filter-label">Horizon</label>
          <p-select [(ngModel)]="daysAhead" [options]="daysAheadOptions"
                    optionLabel="label" optionValue="value" appendTo="body"
                    panelStyleClass="filter-dropdown-panel"
                    (onChange)="loadUpcoming()"></p-select>
        </div>
      </div>

      <div class="filter-bar-actions" *ngIf="hasActiveFilters()">
        <button type="button" class="btn-clear-filters" (click)="clearFilters()">
          <i class="pi pi-filter-slash"></i> Clear
        </button>
      </div>
    </div>

    <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
      <p-tablist>
        <p-tab value="0">Job Runs</p-tab>
        <p-tab value="1">Email Log</p-tab>
        <p-tab value="2">Upcoming Reminders</p-tab>
        <p-tab value="3">Failed Sends</p-tab>
      </p-tablist>

      <p-tabpanels>

        <!-- ============================================ Job runs -->
        <p-tabpanel value="0">
          <p-table
            [scrollable]="true"
            scrollHeight="flex"
            [value]="runs"
            [loading]="runsLoading"
            [lazy]="true"
            (onLazyLoad)="onRunsLazyLoad($event)"
            [paginator]="true"
            [alwaysShowPaginator]="false"
            [rows]="runsRows"
            [totalRecords]="runsTotal"
            [first]="runsFirst"
            [rowsPerPageOptions]="rowsPerPageOptions"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} runs"
            dataKey="id"
            [tableStyle]="{ 'min-width': '100%' }"
            styleClass="p-datatable no-column-lines">

            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="startedAt" style="width: 11rem">
                  Started <p-sortIcon field="startedAt" />
                </th>
                <th pSortableColumn="jobName" style="width: 16rem">
                  Job <p-sortIcon field="jobName" />
                </th>
                <th pSortableColumn="status" style="width: 10rem">
                  Status <p-sortIcon field="status" />
                </th>
                <th pSortableColumn="durationSeconds" style="width: 8rem">
                  Duration <p-sortIcon field="durationSeconds" />
                </th>
                <th style="width: 18rem">Steps</th>
                <th>Error</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-run>
              <tr>
                <td>
                  <div class="cell-date">{{ run.startedAt | date: 'MMM d, y' }}</div>
                  <div class="cell-time">{{ run.startedAt | date: 'HH:mm:ss' }}</div>
                </td>
                <td>{{ run.jobName }}</td>
                <td>
                  <p-tag [value]="run.status" [severity]="runSeverity(run.status)"></p-tag>
                  <span class="attempt-badge" *ngIf="run.attempt > 1">attempt {{ run.attempt }}</span>
                </td>
                <td>{{ formatDuration(run.durationSeconds) }}</td>
                <td class="td-truncate" [pTooltip]="run.stepSummary || ''">
                  {{ run.stepSummary || '—' }}
                </td>
                <td class="td-truncate cell-error" [pTooltip]="run.errorMessage || ''">
                  {{ run.errorMessage || '—' }}
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr class="table-empty-row">
                <td colspan="6" class="table-empty-cell">
                  <div class="empty-state">
                    <i class="pi pi-clock empty-icon"></i>
                    <p class="empty-message">No job runs recorded yet</p>
                    <p class="empty-description">
                      The reminder job records a row each time it runs. If this is still empty
                      after its next scheduled run, the job is not starting.
                    </p>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ============================================ Email log -->
        <p-tabpanel value="1">
          <p-table
            [scrollable]="true"
            scrollHeight="flex"
            [value]="emails"
            [loading]="emailsLoading"
            [lazy]="true"
            (onLazyLoad)="onEmailsLazyLoad($event)"
            [paginator]="true"
            [alwaysShowPaginator]="false"
            [rows]="emailsRows"
            [totalRecords]="emailsTotal"
            [first]="emailsFirst"
            [rowsPerPageOptions]="rowsPerPageOptions"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} emails"
            dataKey="id"
            [tableStyle]="{ 'min-width': '100%' }"
            styleClass="p-datatable no-column-lines">

            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="sentDate" style="width: 11rem">
                  Sent <p-sortIcon field="sentDate" />
                </th>
                <th pSortableColumn="notificationType" style="width: 13rem">
                  Type <p-sortIcon field="notificationType" />
                </th>
                <th pSortableColumn="subject">
                  Subject <p-sortIcon field="subject" />
                </th>
                <th pSortableColumn="recipients" style="width: 18rem">
                  Recipients <p-sortIcon field="recipients" />
                </th>
                <th pSortableColumn="isSent" style="width: 11rem">
                  Outcome <p-sortIcon field="isSent" />
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-email>
              <tr>
                <td>
                  <div class="cell-date">{{ email.sentDate | date: 'MMM d, y' }}</div>
                  <div class="cell-time">{{ email.sentDate | date: 'HH:mm:ss' }}</div>
                </td>
                <td>{{ email.notificationType }}</td>
                <td class="td-truncate" [pTooltip]="email.subject">{{ email.subject }}</td>
                <td class="td-truncate" [pTooltip]="email.recipients">
                  {{ shortRecipients(email.recipients) }}
                </td>
                <td>
                  <p-tag [value]="email.isSent ? 'Sent' : 'Failed'"
                         [severity]="email.isSent ? 'success' : 'danger'"></p-tag>
                  <div class="cell-error" *ngIf="email.errorMessage"
                       [pTooltip]="email.errorMessage">
                    {{ email.errorMessage }}
                  </div>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr class="table-empty-row">
                <td colspan="5" class="table-empty-cell">
                  <div class="empty-state">
                    <i class="pi pi-envelope empty-icon"></i>
                    <p class="empty-message">No emails match these filters</p>
                    <p class="empty-description">Try widening the date range or clearing filters.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ============================================ Upcoming -->
        <p-tabpanel value="2">
          <p-table
            [scrollable]="true"
            scrollHeight="flex"
            [value]="upcoming"
            [loading]="upcomingLoading"
            [paginator]="true"
            [alwaysShowPaginator]="false"
            [rows]="25"
            [rowsPerPageOptions]="rowsPerPageOptions"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} reminders"
            [tableStyle]="{ 'min-width': '100%' }"
            styleClass="p-datatable no-column-lines">

            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="reminderDueOn" style="width: 11rem">
                  Reminder Due <p-sortIcon field="reminderDueOn" />
                </th>
                <th pSortableColumn="clientName">
                  Client <p-sortIcon field="clientName" />
                </th>
                <th pSortableColumn="documentType" style="width: 12rem">
                  Document <p-sortIcon field="documentType" />
                </th>
                <th pSortableColumn="thresholdName" style="width: 10rem">
                  Threshold <p-sortIcon field="thresholdName" />
                </th>
                <th pSortableColumn="daysUntilExpiry" style="width: 11rem">
                  Expires <p-sortIcon field="daysUntilExpiry" />
                </th>
                <th style="width: 9rem">Status</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-r>
              <tr>
                <td>{{ r.reminderDueOn | date: 'MMM d, y' }}</td>
                <td>{{ r.clientName }}</td>
                <td>{{ r.documentType }}</td>
                <td>{{ r.thresholdName }}</td>
                <td>
                  <div class="cell-date">{{ r.expiryDate | date: 'MMM d, y' }}</div>
                  <div class="cell-time">in {{ r.daysUntilExpiry }} days</div>
                </td>
                <td>
                  <p-tag [value]="r.alreadySent ? 'Sent' : 'Pending'"
                         [severity]="upcomingSeverity(r)"></p-tag>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr class="table-empty-row">
                <td colspan="6" class="table-empty-cell">
                  <div class="empty-state">
                    <i class="pi pi-calendar empty-icon"></i>
                    <p class="empty-message">No reminders due in this period</p>
                    <p class="empty-description">
                      No active contract reaches a reminder threshold within the selected horizon.
                    </p>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

        <!-- ============================================ Failed sends -->
        <p-tabpanel value="3">
          <p-table
            [scrollable]="true"
            scrollHeight="flex"
            [value]="failed"
            [loading]="failedLoading"
            [lazy]="true"
            (onLazyLoad)="onFailedLazyLoad($event)"
            [paginator]="true"
            [alwaysShowPaginator]="false"
            [rows]="failedRows"
            [totalRecords]="failedTotal"
            [first]="failedFirst"
            [rowsPerPageOptions]="rowsPerPageOptions"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} failed sends"
            dataKey="id"
            [tableStyle]="{ 'min-width': '100%' }"
            styleClass="p-datatable no-column-lines">

            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="sentDate" style="width: 11rem">
                  Attempted <p-sortIcon field="sentDate" />
                </th>
                <th pSortableColumn="notificationType" style="width: 13rem">
                  Type <p-sortIcon field="notificationType" />
                </th>
                <th pSortableColumn="subject">
                  Subject <p-sortIcon field="subject" />
                </th>
                <th pSortableColumn="recipients" style="width: 18rem">
                  Recipients <p-sortIcon field="recipients" />
                </th>
                <th style="width: 20rem">Error</th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-email>
              <tr>
                <td>
                  <div class="cell-date">{{ email.sentDate | date: 'MMM d, y' }}</div>
                  <div class="cell-time">{{ email.sentDate | date: 'HH:mm:ss' }}</div>
                </td>
                <td>{{ email.notificationType }}</td>
                <td class="td-truncate" [pTooltip]="email.subject">{{ email.subject }}</td>
                <td class="td-truncate" [pTooltip]="email.recipients">
                  {{ shortRecipients(email.recipients) }}
                </td>
                <td class="td-truncate cell-error" [pTooltip]="email.errorMessage || ''">
                  {{ email.errorMessage || '—' }}
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr class="table-empty-row">
                <td colspan="5" class="table-empty-cell">
                  <div class="empty-state">
                    <i class="pi pi-check-circle empty-icon"></i>
                    <p class="empty-message">No failed sends</p>
                    <p class="empty-description">
                      Every email in this range was sent successfully.
                    </p>
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-tabpanel>

      </p-tabpanels>
    </p-tabs>

  </div>
</div>
